import { apiFetch, apiFetchBlob, apiFetchText } from './client'
import type {
  ApiKey,
  Business,
  Call,
  CallDetails,
  CallPromptRequest,
  CompleteFunction,
  CompleteMessage,
  Contact,
  CreateCallRequest,
  Place,
  PriceResponse,
  ProfileResponse,
  SubscriptionResponse,
  VerifyResponse,
  VoicesResponse,
  VoypUser,
} from './types'

export const api = {
  // Profile
  getProfile: () => apiFetch<ProfileResponse>('/api/profile?live=true'),
  updateProfile: (update: Partial<VoypUser> & Record<string, unknown>) =>
    apiFetch<unknown>('/api/profile', { method: 'POST', body: update }),
  getBusiness: () => apiFetch<{ business?: Business }>('/api/profile/business'),
  updateBusiness: (business: Business) =>
    apiFetch<unknown>('/api/profile/business', { method: 'POST', body: business }),
  revokeContacts: () => apiFetch<unknown>('/api/profile/contacts', { method: 'DELETE' }),
  revokeCalendar: () => apiFetch<unknown>('/api/profile/calendar', { method: 'DELETE' }),
  logout: (machineId: string) => apiFetch<unknown>(`/api/logout/${machineId}`, { method: 'POST' }),

  // Calls — list/detail responses come wrapped: {calls} / {call}
  getCalls: () => apiFetch<{ calls?: Call[] }>('/api/calls').then((r) => r.calls ?? []),
  searchCalls: (search: string) =>
    apiFetch<{ calls?: Call[] }>(
      `/api/calls/search?search=${encodeURIComponent(search)}&limit=100`,
    ).then((r) => r.calls ?? []),
  getCall: (callSid: string) =>
    apiFetch<{ call?: Call }>(`/api/calls/${callSid}`).then((r) => {
      if (!r.call) throw new Error('Call not found')
      return r.call
    }),
  createCall: (request: CreateCallRequest) =>
    apiFetch<{ call: string }>('/api/calls/create', { method: 'POST', body: request }),
  hangupCall: (callSid: string) =>
    apiFetch<unknown>(`/api/calls/hangup/${callSid}`, { method: 'POST' }),
  deleteCall: (callSid: string) =>
    apiFetch<unknown>(`/api/calls/${callSid}/delete`, { method: 'POST' }),
  // Live call details come wrapped: {details}
  getCallDetails: (callSid: string) =>
    apiFetch<{ details?: CallDetails }>(`/api/call/details/${callSid}`).then((r) => {
      if (!r.details) throw new Error('Call not found')
      return r.details
    }),
  updateCallDetails: (callSid: string, details: CallDetails) =>
    apiFetch<{ details?: CallDetails }>(`/api/call/details/${callSid}`, {
      method: 'POST',
      body: details,
    }).then((r) => r.details ?? {}),
  forwardCall: (callSid: string) =>
    apiFetch<unknown>(`/api/call/forward/${callSid}`, { method: 'POST' }),
  speak: (callSid: string, text: string) =>
    apiFetch<unknown>(`/api/call/speak/${callSid}`, { method: 'POST', body: { text } }),
  copilotReply: (callSid: string, reply: string, id: string) =>
    apiFetch<unknown>(`/api/call/copilot/${callSid}`, { method: 'POST', body: { reply, id } }),

  // Caller ID verification
  verifyNumber: (number: string) =>
    apiFetch<VerifyResponse>('/api/caller_id/verify', { method: 'POST', body: { number } }),
  verifyCode: (code: string, id: string) =>
    apiFetch<VerifyResponse>('/api/caller_id/code', { method: 'POST', body: { code, id } }),

  // AI / content
  complete: (messages: CompleteMessage[], functions?: CompleteFunction[], engine = 'chatgpt') =>
    apiFetch<Record<string, unknown>>('/api/complete', {
      method: 'POST',
      body: { engine, messages, functions },
    }),
  callPrompt: (request: CallPromptRequest) =>
    apiFetch<{ prompt?: string }>('/api/prompt', { method: 'POST', body: request }),
  promptContext: (number?: string) =>
    apiFetch<Record<string, unknown>>('/api/prompt/context', {
      method: 'POST',
      body: { engine: 'chatgpt', number },
    }),
  translate: (source: string, target: string, text: string) =>
    apiFetch<{ text?: string; translation?: string }>(`/api/translate/${source}/${target}`, {
      method: 'POST',
      body: { text },
    }),
  summarizeUrl: (url: string) =>
    apiFetch<{ summary?: string }>('/api/summary', { method: 'POST', body: { url } }),
  say: (text: string, engine: string, language: string, voice: string) =>
    apiFetchBlob('/api/say', { method: 'POST', body: { text, engine, language, voice } }),
  sayHash: (hash: string) => apiFetchBlob(`/api/say/${hash}`),
  getVoices: () => apiFetch<VoicesResponse>('/api/voices'),
  getPrice: (e164: string, advanced: boolean, record?: boolean) =>
    apiFetch<PriceResponse>(
      `/api/price/${encodeURIComponent(e164)}?advanced=${advanced}` +
        (record === undefined ? '' : `&record=${record}`),
    ),

  // Places & contacts
  searchPlaces: (query: string) =>
    apiFetch<Place[] | Record<string, unknown>>(`/api/places/search?query=${encodeURIComponent(query)}`),
  getPlace: (place: string, location?: string) =>
    apiFetch<Place | Record<string, unknown>>(
      `/api/place/search?place=${encodeURIComponent(place)}&location=${encodeURIComponent(location ?? '')}`,
    ),
  getContacts: () => apiFetch<{ contacts?: Contact[] }>('/api/contacts'),
  searchContacts: (search: string) =>
    apiFetch<{ contacts?: Contact[] } | Contact[]>(
      `/api/contacts/search?search=${encodeURIComponent(search)}`,
    ),
  getContactRelationships: () => apiFetch<unknown>('/api/contacts/relationships'),

  // Billing
  getSubscription: () => apiFetch<SubscriptionResponse>('/api/subscription'),
  getBusinessSubscription: () => apiFetch<SubscriptionResponse>('/api/subscription/business'),

  // API keys — every endpoint returns the updated {keys} list
  getKeys: () => apiFetch<{ keys?: ApiKey[] }>('/api/keys'),
  createKey: () => apiFetch<{ keys?: ApiKey[] }>('/api/keys/create', { method: 'POST' }),
  deleteKey: (key: string) =>
    apiFetch<{ keys?: ApiKey[] }>('/api/keys/delete', { method: 'POST', body: { key } }),

  // Feed
  getFeed: () => apiFetchText('/api/feed'),
}
